import React from 'react';

import type { WalletType } from 'types/client/wallets';
import type { WalletProvider } from 'types/web3';

import config from 'configs/app';
import { getFeaturePayload } from 'configs/app/features/types';

const feature = config.features.web3Wallet;
const web3WalletFeature = getFeaturePayload(feature);

function useProviderFallback() {
  return { provider: undefined, wallet: undefined };
}

function useProviderActive() {
  const [ provider, setProvider ] = React.useState<WalletProvider>();
  const [ wallet, setWallet ] = React.useState<WalletType>();

  const initializeProvider = React.useMemo(() => async() => {
    if (!('ethereum' in window && window.ethereum)) {
      if (web3WalletFeature?.wallets.includes('metamask') && window.navigator.userAgent.includes('Firefox')) {
        const { WindowPostMessageStream } = (await import('@metamask/post-message-stream'));
        const { initializeProvider } = (await import('@metamask/providers'));

        const metamaskStream = new WindowPostMessageStream({
          name: 'metamask-inpage',
          target: 'metamask-contentscript',
        });

        initializeProvider({
          connectionStream: metamaskStream as never,
          shouldShimWeb3: true,
        });
      } else {
        return;
      }
    }

    if (!('ethereum' in window && window.ethereum)) {
      return;
    }

    const providers = Array.isArray(window.ethereum.providers) ? window.ethereum.providers : [ window.ethereum ];

    for (const walletType of web3WalletFeature?.wallets ?? []) {
      const provider = providers.find((provider) => {
        return (
          (walletType === 'metamask' && provider.isMetaMask && Boolean(provider._events)) ||
          (walletType === 'coinbase' && provider.isCoinbaseWallet) ||
          (walletType === 'token_pocket' && provider.isTokenPocket)
        );
      });

      if (provider) {
        setProvider(provider);
        setWallet(walletType);
        break;
      }
    }
  }, []);

  React.useEffect(() => {
    initializeProvider();
  }, [ initializeProvider ]);

  return { provider, wallet };
}

const useProvider = feature.isEnabled ? useProviderActive : useProviderFallback;

export default useProvider;
